import React from "react";
import { Lock, ShieldCheck, BadgeCheck, FileLock2, Check, Save } from "lucide-react";

export const TRUST_BADGES = [
  { icon: Lock, label: "256-bit bank-grade encryption" },
  { icon: BadgeCheck, label: "Regulated conduct — QFC / DFSA aligned" },
  { icon: FileLock2, label: "ISO 27001 information security" },
];

export const TrustStrip = ({ compact }) => (
  <div className={`flex flex-wrap gap-x-5 gap-y-2 ${compact ? "text-[11px]" : "text-xs"}`} data-testid="kyc-trust-strip">
    {TRUST_BADGES.map((b) => (
      <span key={b.label} className="flex items-center gap-1.5 text-muted-foreground">
        <b.icon className="h-3.5 w-3.5 text-[color:var(--tharwy-lime-700)]" />{b.label}
      </span>
    ))}
    <a href="/legal/privacy" target="_blank" rel="noreferrer"
      className="flex items-center gap-1.5 font-semibold text-foreground underline underline-offset-2 hover:text-[color:var(--tharwy-lime-700)]"
      data-testid="kyc-privacy-link">
      Privacy policy
    </a>
  </div>
);

/** Left rail: institutional navy, phase list, live progress and trust proof. */
export const OnboardingRail = ({ phases, currentPhase, pct, savedAt }) => (
  <aside className="relative hidden lg:flex flex-col justify-between p-10 overflow-hidden text-white"
    style={{ background: "linear-gradient(165deg, #120D22 0%, #14203A 55%, #1B2A48 100%)" }}
    data-testid="onboarding-rail">
    <div className="absolute inset-0 opacity-40"
      style={{ background: "radial-gradient(900px circle at 15% 5%, rgba(167,198,63,0.18), transparent 55%)" }} />
    <div className="relative z-10">
      <div className="flex items-center gap-2.5">
        <img src="/tharwy-logo-light.png" alt="Tharwy — your wealth never sleeps." className="h-16 w-auto object-contain" data-testid="tharwy-logo" />
        <span className="font-heading text-xl font-bold tracking-tight">Tharwy</span>
      </div>

      <h1 className="mt-10 font-heading text-[26px] font-bold leading-tight">Open your account</h1>
      <p className="mt-3 text-white/60 text-sm leading-relaxed max-w-xs">
        Four short stages. Everything is saved as you go, so you can stop and pick up later.
      </p>

      <div className="mt-8">
        <div className="flex items-center justify-between text-xs font-semibold">
          <span className="text-white/70">Application progress</span>
          <span className="numeric text-[color:var(--tharwy-lime)]" data-testid="onboarding-progress-pct">{pct}%</span>
        </div>
        <div className="mt-2 h-2 w-full rounded-full bg-white/10 overflow-hidden">
          <div className="h-full rounded-full bg-[color:var(--tharwy-lime)] transition-[width] duration-500"
            style={{ width: `${pct}%` }} data-testid="onboarding-progress" />
        </div>
      </div>

      <ol className="mt-10 space-y-5">
        {phases.map((p, i) => {
          const done = i < currentPhase, current = i === currentPhase;
          return (
            <li key={p.label} className="flex items-start gap-3.5">
              <span className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 transition-colors ${
                done ? "bg-[color:var(--tharwy-lime)] text-[#120D22]"
                     : current ? "bg-white text-[#120D22]" : "bg-white/10 text-white/50"}`}>
                {done ? <Check className="h-4 w-4" /> : i + 1}
              </span>
              <span className="pt-1">
                <span className={`block text-sm font-semibold ${current ? "text-white" : done ? "text-white/75" : "text-white/45"}`}>
                  {p.label}
                </span>
                <span className={`block text-[11px] mt-0.5 ${current ? "text-white/60" : "text-white/35"}`}>{p.hint}</span>
              </span>
            </li>
          );
        })}
      </ol>
    </div>

    <div className="relative z-10 space-y-3">
      {savedAt && (
        <div className="flex items-center gap-1.5 text-[11px] text-white/50" data-testid="onboarding-saved-rail">
          <Save className="h-3.5 w-3.5" /> Progress saved {new Date(savedAt).toLocaleTimeString()}
        </div>
      )}
      <div className="space-y-2">
        {TRUST_BADGES.map((b) => (
          <div key={b.label} className="flex items-center gap-2 text-[11px] text-white/55">
            <b.icon className="h-3.5 w-3.5 text-[color:var(--tharwy-lime)]" />{b.label}
          </div>
        ))}
      </div>
      <a href="/legal/privacy" target="_blank" rel="noreferrer"
        className="inline-flex items-center gap-1.5 text-[11px] text-white/70 underline underline-offset-2 hover:text-white">
        <ShieldCheck className="h-3.5 w-3.5" /> How we protect your data
      </a>
    </div>
  </aside>
);
